
/**
 * The design stage. AC-2.1.
 *
 * Drag, scale and clamp behave exactly as the approved prototype: the pointer
 * position is converted to a percentage of the print zone and clamped to
 * [0, 100] on both axes, so the design's centre can reach the zone edge but not
 * leave it. The three numbers this produces, {pos_x, pos_y, scale}, are the
 * whole placement contract shared with the Flutter app.
 *
 * Pointer events cover mouse, pen and touch in one path, `touch-none` stops the
 * browser scrolling the page mid-drag, and the arrow keys move the design for
 * anyone not using a pointer at all.
 */

import { useCallback, useRef } from 'react';

import { GARMENTS, overlayBox, shade } from '@/components/garment-mockup';
import type { GarmentType, Placement } from '@/lib/types';

const clamp = (n: number) => Math.max(0, Math.min(100, n));

export function DesignStage({
  type,
  colorHex,
  designUrl,
  placement,
  onPlace,
}: {
  type: GarmentType;
  colorHex: string;
  designUrl: string | null;
  placement: Placement;
  onPlace: (next: Placement) => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const garment = GARMENTS[type] ?? GARMENTS.tee;
  const zone = garment.zone;
  const { size, cx, cy } = overlayBox(zone, placement);

  const moveTo = useCallback(
    (clientX: number, clientY: number) => {
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const zoneX = (zone.x / 100) * rect.width;
      const zoneY = (zone.y / 100) * rect.height;
      const zoneW = (zone.w / 100) * rect.width;
      const zoneH = (zone.h / 100) * rect.height;

      onPlace({
        ...placement,
        pos_x: clamp(((clientX - rect.left - zoneX) / zoneW) * 100),
        pos_y: clamp(((clientY - rect.top - zoneY) / zoneH) * 100),
      });
    },
    [onPlace, placement, zone],
  );

  return (
    <div className="rounded-[10px] border border-line bg-panel p-5">
      <div
        ref={stageRef}
        className="checkerboard relative aspect-square w-full overflow-hidden rounded-[10px] border border-line"
      >
        <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full" aria-hidden="true">
          {garment.paths(colorHex)}
        </svg>

        {/* The print area, drawn so people can see what they are aiming at. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-[3px] border border-dashed"
          style={{
            left: `${zone.x}%`,
            top: `${zone.y}%`,
            width: `${zone.w}%`,
            height: `${zone.h}%`,
            borderColor: shade(colorHex, -60),
            opacity: 0.55,
          }}
        />

        {designUrl && (
          <div
            role="application"
            aria-label="Design position. Drag, or use the arrow keys to nudge."
            tabIndex={0}
            className="absolute cursor-grab touch-none select-none rounded-[2px] active:cursor-grabbing focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            style={{
              width: `${size}%`,
              height: `${size}%`,
              left: `${cx}%`,
              top: `${cy}%`,
              transform: 'translate(-50%, -50%)',
            }}
            onPointerDown={(e) => {
              dragging.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              if (!dragging.current) return;
              moveTo(e.clientX, e.clientY);
            }}
            onPointerUp={() => {
              dragging.current = false;
            }}
            onPointerCancel={() => {
              dragging.current = false;
            }}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 8 : 2;
              const nudge: Record<string, Partial<Placement>> = {
                ArrowLeft: { pos_x: clamp(placement.pos_x - step) },
                ArrowRight: { pos_x: clamp(placement.pos_x + step) },
                ArrowUp: { pos_y: clamp(placement.pos_y - step) },
                ArrowDown: { pos_y: clamp(placement.pos_y + step) },
              };
              const delta = nudge[e.key];
              if (!delta) return;
              e.preventDefault();
              onPlace({ ...placement, ...delta });
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={designUrl}
              alt=""
              draggable={false}
              className="pointer-events-none h-full w-full object-contain"
              style={{ filter: 'drop-shadow(0 2px 4px rgb(0 0 0 / 0.18))' }}
            />
          </div>
        )}
      </div>

      <p className="mt-3 text-center text-sm text-muted">
        {designUrl
          ? 'Drag the design to reposition it within the print area.'
          : 'Pick a ready-made design or upload your own to see it on the garment.'}
      </p>
    </div>
  );
}
